import {useAuth} from 'react-oidc-context';
import {useState, useEffect, useCallback, useMemo} from 'react';
import {decodeJwtPayload} from './tokenUtils';
import {derivePrimaryRole, effectiveRoles, isPlatformAdminRole} from './roles';

const ROLE_PROXY_URL = process.env.REACT_APP_ROLE_PROXY_URL || '';

const useAuthUser = () => {
  const auth = useAuth();
  const [rolesByProject, setRolesByProject] = useState({});
  const [allRoles, setAllRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState(null);

  const fetchRoles = useCallback(async (accessToken) => {
    if (!ROLE_PROXY_URL || !accessToken) return;
    setRolesLoading(true);
    setRolesError(null);
    try {
      const resp = await fetch(`${ROLE_PROXY_URL}/api/roles`, {
        headers: {Authorization: `Bearer ${accessToken}`}
      });
      if (!resp.ok) throw new Error(`Role fetch failed: ${resp.status}`);
      const data = await resp.json();
      setRolesByProject(data.roles || {});
      setAllRoles(data.allRoles || []);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      setRolesError(err.message);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user?.access_token) {
      // Try JWT claims first, fall back to role proxy
      const claims = decodeJwtPayload(auth.user.access_token) || {};
      const tokenRoles = effectiveRoles(claims);
      if (tokenRoles.length > 0) {
        setAllRoles(tokenRoles);
      } else if (ROLE_PROXY_URL) {
        fetchRoles(auth.user.access_token);
      }
    }
  }, [auth.isAuthenticated, auth.user?.access_token, fetchRoles]);

  // Extract org info from access token JWT
  const tokenClaims = useMemo(() => {
    if (!auth.user?.access_token) return {};
    return decodeJwtPayload(auth.user.access_token) || {};
  }, [auth.user?.access_token]);

  const orgId = tokenClaims.org_id || '';
  const orgName = tokenClaims.org_name || '';

  const roles = allRoles;
  const role = derivePrimaryRole(roles);
  const isPlatformAdmin = isPlatformAdminRole(role);

  return {
    user: auth.user,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading || rolesLoading,
    error: auth.error || rolesError,
    accessToken: auth.user?.access_token || '',
    roles,
    rolesByProject,
    role,
    orgId,
    orgName,
    isPlatformAdmin,
    hasRole: (r) => roles.includes(r),
    hasProjectRole: (project, r) => (rolesByProject[project] || []).includes(r),
    login: () => auth.signinRedirect(),
    logout: () => auth.signoutRedirect()
  };
};

export default useAuthUser;
